import type { ICoord2D, IRect } from '@nova/types';

import { clampInt } from '../math-utils';
import {
  chooseAspectAwareGridDimensions,
  clampRectInsideRegion,
  expandRectToContain,
  normalizeRectToIntegerTiles,
  pointInRect,
  rectCenter,
  rectContainsRect,
  rectsShareEdge,
  unionRects
} from '../rect-utils';
import { randomIntInclusive, type SeededRandom } from '../seeded-random';

export interface PartitionInteriorByAnchorBoxArgs {
  region: IRect;
  /** Reserved POI footprint; a ~1/N area box is wrapped around its center to seed map cuts. */
  anchor: IRect;
  /** Total POI cells including the anchor (pad count + 1). */
  poiCellCount: number;
  minCellWidth: number;
  minCellHeight: number;
  jitterFraction?: number;
  rand?: SeededRandom;
}

export interface PartitionInteriorByAnchorBoxResult {
  anchorZone: IRect;
  padZones: IRect[];
  /** Every partition cell after merge (anchor + pads), for debug overlays. */
  allCells: IRect[];
}

/**
 * Partitions the map interior into `poiCellCount` axis-aligned zones:
 * 1. Wrap the anchor in a box of roughly 1/N map area, centered on the anchor.
 * 2. Extend the box edges as full-map cuts, then add evenly spaced cuts until cell count >= N.
 * 3. Jitter internal cuts that do not touch the anchor box.
 * 4. Merge excess cells (smallest into a neighbor) until exactly N remain.
 */
export function partitionInteriorByAnchorBox(
  args: PartitionInteriorByAnchorBoxArgs
): PartitionInteriorByAnchorBoxResult | null {
  const poiCellCount = Math.max(1, Math.floor(args.poiCellCount));
  const minCellWidth = Math.max(1, Math.floor(args.minCellWidth));
  const minCellHeight = Math.max(1, Math.floor(args.minCellHeight));
  const jitterFraction = Math.max(0, args.jitterFraction ?? 0);
  const region = normalizeRectToIntegerTiles(args.region);
  const anchor = normalizeRectToIntegerTiles(args.anchor);

  if (region.width < minCellWidth || region.height < minCellHeight) {
    return null;
  }

  const anchorBox = buildAnchorPartitionBox({
    region,
    anchor,
    poiCellCount,
    minCellWidth,
    minCellHeight
  });
  if (!anchorBox) {
    return null;
  }

  let xCuts = buildInitialAxisCuts(region.x, region.x + region.width, anchorBox.x, anchorBox.x + anchorBox.width);
  let yCuts = buildInitialAxisCuts(region.y, region.y + region.height, anchorBox.y, anchorBox.y + anchorBox.height);

  addSupplementalAxisCuts({
    xCuts,
    yCuts,
    region,
    poiCellCount,
    minCellWidth,
    minCellHeight
  });

  if (args.rand && jitterFraction > 0) {
    xCuts = jitterAxisCuts({
      cuts: xCuts,
      regionMin: region.x,
      regionMax: region.x + region.width,
      protectedValues: [anchorBox.x, anchorBox.x + anchorBox.width],
      minSegment: minCellWidth,
      jitterFraction,
      rand: args.rand
    });
    yCuts = jitterAxisCuts({
      cuts: yCuts,
      regionMin: region.y,
      regionMax: region.y + region.height,
      protectedValues: [anchorBox.y, anchorBox.y + anchorBox.height],
      minSegment: minCellHeight,
      jitterFraction,
      rand: args.rand
    });
  }

  let cells = buildCellsFromAxisCuts(xCuts, yCuts, minCellWidth, minCellHeight);
  if (cells.length === 0) {
    return null;
  }

  const anchorCenter = rectCenter(anchor);
  cells = mergeCellsToCount(cells, poiCellCount, anchorCenter);
  if (cells.length !== poiCellCount) {
    return null;
  }

  const anchorZoneIndex = findCellIndexContainingPoint(cells, anchorCenter);
  if (anchorZoneIndex === null) {
    return null;
  }

  const anchorZone = cells[anchorZoneIndex];
  const padZones = cells.filter((_, index) => index !== anchorZoneIndex);
  if (padZones.length !== poiCellCount - 1) {
    return null;
  }

  if (!padZones.every((zone) => zone.width >= minCellWidth && zone.height >= minCellHeight)) {
    return null;
  }

  return {
    anchorZone,
    padZones,
    allCells: [...cells]
  };
}

interface BuildAnchorPartitionBoxArgs {
  region: IRect;
  anchor: IRect;
  poiCellCount: number;
  minCellWidth: number;
  minCellHeight: number;
}

function buildAnchorPartitionBox(args: BuildAnchorPartitionBoxArgs): IRect | null {
  const { region, anchor, poiCellCount, minCellWidth, minCellHeight } = args;
  const targetArea = (region.width * region.height) / poiCellCount;
  const regionAspect = region.width / Math.max(1, region.height);

  let boxHeight = Math.sqrt(targetArea / regionAspect);
  let boxWidth = targetArea / Math.max(1, boxHeight);

  boxWidth = Math.max(boxWidth, anchor.width, minCellWidth);
  boxHeight = Math.max(boxHeight, anchor.height, minCellHeight);

  const center = rectCenter(anchor);
  let box: IRect = {
    x: Math.floor(center.x - (boxWidth / 2)),
    y: Math.floor(center.y - (boxHeight / 2)),
    width: Math.ceil(boxWidth),
    height: Math.ceil(boxHeight)
  };

  box = clampRectInsideRegion(box, region);
  if (!rectContainsRect(box, anchor)) {
    box = expandRectToContain(box, anchor, region);
  }

  if (box.width < minCellWidth || box.height < minCellHeight) {
    return null;
  }

  return box;
}

function buildInitialAxisCuts(regionMin: number, regionMax: number, boxMin: number, boxMax: number): number[] {
  const cuts = [regionMin, regionMax];
  if (boxMin > regionMin && boxMin < regionMax) {
    cuts.push(boxMin);
  }

  if (boxMax > regionMin && boxMax < regionMax) {
    cuts.push(boxMax);
  }
  return uniqueSortedInts(cuts);
}

interface AddSupplementalAxisCutsArgs {
  xCuts: number[];
  yCuts: number[];
  region: IRect;
  poiCellCount: number;
  minCellWidth: number;
  minCellHeight: number;
}

function addSupplementalAxisCuts(args: AddSupplementalAxisCutsArgs): void {
  const { region, poiCellCount } = args;
  const grid = chooseAspectAwareGridDimensions(poiCellCount, region.width, region.height);

  let guard = 0;
  while (cellCountFromCuts(args.xCuts, args.yCuts) < poiCellCount && guard < 64) {
    guard += 1;

    const targetVerticalCuts = grid.col + 1;
    const targetHorizontalCuts = grid.row + 1;
    const verticalDeficit = targetVerticalCuts - args.xCuts.length;
    const horizontalDeficit = targetHorizontalCuts - args.yCuts.length;

    if (verticalDeficit > 0 && (verticalDeficit >= horizontalDeficit || horizontalDeficit <= 0)) {
      const added = addEvenAxisCut({
        cuts: args.xCuts,
        regionMin: region.x,
        regionMax: region.x + region.width,
        segments: grid.col
      });
      if (!added) {
        break;
      }
      continue;
    }

    if (horizontalDeficit > 0) {
      const added = addEvenAxisCut({
        cuts: args.yCuts,
        regionMin: region.y,
        regionMax: region.y + region.height,
        segments: grid.row
      });
      if (!added) {
        break;
      }
      continue;
    }

    if (cellCountFromCuts(args.xCuts, args.yCuts) < poiCellCount) {
      const addedVertical = addEvenAxisCut({
        cuts: args.xCuts,
        regionMin: region.x,
        regionMax: region.x + region.width,
        segments: args.xCuts.length
      });
      if (addedVertical) {
        continue;
      }

      const addedHorizontal = addEvenAxisCut({
        cuts: args.yCuts,
        regionMin: region.y,
        regionMax: region.y + region.height,
        segments: args.yCuts.length
      });
      if (!addedHorizontal) {
        break;
      }
    }
  }
}

interface AddEvenAxisCutArgs {
  cuts: number[];
  regionMin: number;
  regionMax: number;
  segments: number;
}

function addEvenAxisCut(args: AddEvenAxisCutArgs): boolean {
  const segmentCount = Math.max(2, args.segments);
  const span = args.regionMax - args.regionMin;
  if (span <= 0) {
    return false;
  }

  for (let index = 1; index < segmentCount; index++) {
    const candidate = Math.round(args.regionMin + ((span * index) / segmentCount));
    if (candidate <= args.regionMin || candidate >= args.regionMax) {
      continue;
    }

    if (args.cuts.includes(candidate)) {
      continue;
    }

    args.cuts.push(candidate);
    args.cuts.sort((left, right) => left - right);
    return true;
  }

  return false;
}

interface JitterAxisCutsArgs {
  cuts: number[];
  regionMin: number;
  regionMax: number;
  protectedValues: readonly number[];
  minSegment: number;
  jitterFraction: number;
  rand: SeededRandom;
}

function jitterAxisCuts(args: JitterAxisCutsArgs): number[] {
  const jittered = [...args.cuts];
  for (let index = 1; index < jittered.length - 1; index++) {
    const cut = jittered[index];
    if (args.protectedValues.some((value) => value === cut)) {
      continue;
    }

    const prev = jittered[index - 1];
    const next = jittered[index + 1];
    const leftSlack = Math.max(0, cut - prev - args.minSegment);
    const rightSlack = Math.max(0, next - cut - args.minSegment);
    const maxShift = Math.floor(Math.min(leftSlack, rightSlack) * args.jitterFraction);
    if (maxShift <= 0) {
      continue;
    }

    jittered[index] = clampInt(cut + randomIntInclusive(args.rand, -maxShift, maxShift), prev + args.minSegment, next - args.minSegment);
  }

  return uniqueSortedInts(jittered);
}

function buildCellsFromAxisCuts(
  xCuts: readonly number[],
  yCuts: readonly number[],
  minCellWidth: number,
  minCellHeight: number
): IRect[] {
  const cells: IRect[] = [];

  for (let row = 0; row < yCuts.length - 1; row++) {
    for (let col = 0; col < xCuts.length - 1; col++) {
      const width = xCuts[col + 1] - xCuts[col];
      const height = yCuts[row + 1] - yCuts[row];
      if (width < minCellWidth || height < minCellHeight) {
        continue;
      }

      cells.push({
        x: xCuts[col],
        y: yCuts[row],
        width,
        height
      });
    }
  }

  return cells;
}

function mergeCellsToCount(cells: IRect[], targetCount: number, anchorCenter: ICoord2D): IRect[] {
  let merged = [...cells];
  let guard = 0;

  while (merged.length > targetCount && guard < 256) {
    guard += 1;
    const anchorIndex = findCellIndexContainingPoint(merged, anchorCenter);

    let mergeIndex = -1;
    let mergeNeighbor = -1;
    let smallestArea = Number.POSITIVE_INFINITY;

    for (let index = 0; index < merged.length; index++) {
      if (index === anchorIndex) {
        continue;
      }

      const area = merged[index].width * merged[index].height;
      if (area >= smallestArea) {
        continue;
      }

      const neighborIndex = findSmallestAdjacentCellIndex(merged, index);
      if (neighborIndex === null) {
        continue;
      }

      smallestArea = area;
      mergeIndex = index;
      mergeNeighbor = neighborIndex;
    }

    if (mergeIndex === -1 || mergeNeighbor === -1) {
      mergeIndex = findSmallestCellIndex(merged);
      const fallbackNeighbor = findSmallestAdjacentCellIndex(merged, mergeIndex);
      if (fallbackNeighbor === null) {
        break;
      }
      mergeNeighbor = fallbackNeighbor;
    }

    const mergedCell = unionRects(merged[mergeIndex], merged[mergeNeighbor]);
    merged = merged.filter((_, index) => index !== mergeIndex && index !== mergeNeighbor);
    merged.push(mergedCell);
  }

  return merged;
}

function findSmallestCellIndex(cells: readonly IRect[]): number {
  let bestIndex = 0;
  let bestArea = cells[0].width * cells[0].height;
  for (let index = 1; index < cells.length; index++) {
    const area = cells[index].width * cells[index].height;
    if (area < bestArea) {
      bestArea = area;
      bestIndex = index;
    }
  }
  return bestIndex;
}

function findSmallestAdjacentCellIndex(cells: readonly IRect[], index: number): number | null {
  const source = cells[index];
  let bestIndex: number | null = null;
  let bestArea = Number.POSITIVE_INFINITY;

  for (let candidate = 0; candidate < cells.length; candidate++) {
    if (candidate === index || !rectsShareEdge(cells[candidate], source)) {
      continue;
    }

    const area = cells[candidate].width * cells[candidate].height;
    if (area < bestArea) {
      bestArea = area;
      bestIndex = candidate;
    }
  }

  return bestIndex;
}

function findCellIndexContainingPoint(cells: readonly IRect[], point: ICoord2D): number | null {
  for (let index = 0; index < cells.length; index++) {
    if (pointInRect(point, cells[index])) {
      return index;
    }
  }
  return null;
}

function uniqueSortedInts(values: readonly number[]): number[] {
  return [...new Set(values.map((value) => Math.round(value)))].sort((left, right) => left - right);
}

function cellCountFromCuts(xCuts: readonly number[], yCuts: readonly number[]): number {
  return Math.max(0, (xCuts.length - 1) * (yCuts.length - 1));
}
